import { z } from "zod";
import { AccountKind, Transaction } from "../domain.js";
import { Id, IdList, IsoDate, IsoMonth, IsoTimestamp, Pence } from "../primitives.js";
import { RangePeriod, ResolvedPeriod } from "./query.js";

/**
 * A single month, or one of the picker's ranges — exactly one, so a request
 * naming neither or both is a validation error rather than a guess at which
 * one was meant.
 *
 * `accountIds` narrows every figure to those accounts. Absent means all of
 * them, the same convention a budget's `accountIds` uses: the household that
 * has never wanted to look at one account on its own never meets the field.
 */
export const DashboardQuery = z
  .object({
    month: IsoMonth.optional(),
    period: RangePeriod.optional(),
    accountIds: IdList.optional(),
  })
  .refine((query) => (query.month === undefined) !== (query.period === undefined), {
    message: "Give a month or a period, not both or neither.",
  });

/**
 * The month in four figures.
 *
 * The three money ones are decided by what a transaction **is** — the kind of
 * the category it is filed under — rather than by which way its amount points.
 * A refund filed under Groceries is negative spending, not income. A row
 * nobody has filed yet has no kind to go on, so its sign is used.
 *
 * Between them they cover every transaction except the transfers, so
 * `netPence` is `in + out + saving` and is the month's real movement.
 * `count` is every row including the transfers, and `transferCount` is that
 * gap — stated, so a screen showing rows and totals together can explain why
 * one is larger.
 */
export const MonthTotals = z.object({
  inPence: Pence,
  outPence: Pence,
  /** Money deliberately set aside: categories of kind `saving`. */
  savingPence: Pence,
  netPence: Pence,
  count: z.number().int(),
  transferCount: z.number().int(),
});

export const CategorySlice = z.object({
  categoryId: Id.nullable(),
  name: z.string(),
  spentPence: Pence,
  txnCount: z.number().int(),
  colour: z.string().nullable(),
});

export const AccountSummary = z.object({
  id: Id,
  key: z.string(),
  label: z.string(),
  kind: AccountKind,
  balancePence: Pence.nullable(),
  balanceUpdatedAt: IsoTimestamp.nullable(),
  monthInPence: Pence,
  monthOutPence: Pence,
  /**
   * Everything that moved through the account in the period — and the one
   * figure in the app that counts transfers, because it says where the money
   * ended up rather than what the household earned or spent. An account that
   * sent £1,200 to savings is £1,200 lighter whatever the transfer was called.
   */
  monthNetPence: Pence,
  txnCount: z.number().int(),
  /** The last row in view, for judging whether a statement is missing its tail. */
  lastTransactionDate: IsoDate.nullable(),
});

/**
 * The partial state, made explicit.
 *
 * A finance app hits this constantly: this account's statement is in, that
 * one's is not. The dashboard shows the numbers it has and names what is
 * missing, because a total that quietly omits an account is worse than no
 * total at all.
 *
 * Measured over the accounts the figures are about, so focusing on the one
 * account whose statement is in stops the screen nagging about the two it is
 * no longer counting.
 */
export const Coverage = z.object({
  accountsWithData: z.number().int(),
  accountsTotal: z.number().int(),
  /**
   * Accounts with no rows at all in view: a statement nobody has uploaded.
   *
   * `kind` comes along because it changes what the gap means. An unimported
   * savings account costs the figures a transfer nobody counted anyway; an
   * unimported **credit card** costs them every purchase made on it, while the
   * payments that cleared it sit in the ledger as transfers going nowhere. That
   * is worth saying in different words.
   */
  missing: z.array(z.object({ id: Id, label: z.string(), kind: AccountKind })),
  /**
   * Accounts with *some* rows in view whose last one falls well short of the
   * furthest the household has imported — a statement missing its tail.
   *
   * These passed the check above and so read as complete. A month whose statement
   * covers five days of thirty-one is the case this exists for: every figure on
   * the screen is right about the rows it has and wrong about the month.
   */
  behind: z.array(z.object({ id: Id, label: z.string(), lastTransactionDate: IsoDate.nullable() })),
});

/**
 * Transfer rows no total counts and nobody has classified.
 *
 * Every money figure on this screen drops rows whose category is of kind
 * `transfer`, because a payment written down twice would otherwise invent income
 * and spending. That is correct when both halves are in the ledger. When only one
 * is — money from an account nobody imports, a payment to a card nobody imports —
 * the row is real income or real spending, and dropping it understates the period
 * by exactly its amount.
 *
 * This is that amount. It is not an error count and it is not expected to reach
 * zero: some accounts will never be imported, so an unmatched leg is a thing to
 * be classified rather than a queue that drains.
 */
export const Unclassified = z.object({
  count: z.number().int(),
  /** Signed net. What the figures are out by if all of it turns out to be real. */
  pence: Pence,
});

/**
 * How this period's "left over" sits against every month that came before it
 * — the fact the isolated monthly view cannot show on its own.
 *
 * A household that saved £400 in June and overspent by £150 in July is not
 * £150 down: it is £250 up, because June's surplus is still sitting in the
 * accounts. `carriedInPence` is the running position the moment this period
 * began; `runningPence` is `carriedInPence + totals.netPence`, the same
 * position at the end of it. Both are zero for all-time, and `carriedInPence`
 * is zero for any period that starts at the first month the ledger has data
 * in — there is nothing before either to carry.
 */
export const RunningTotal = z.object({
  carriedInPence: Pence,
  runningPence: Pence,
});

/**
 * One bucket of the period's own flow: what moved inside it.
 *
 * The three money figures are `MonthTotals`' three, decided by the kind of the
 * category a row is filed under and covering every row that is not a transfer
 * — so the points of a period **sum to** its totals, one figure at a time, and
 * a chart drawn from these cannot disagree with the figures beside it.
 *
 * Signed as the ledger stores it, and as `MonthTotals` reports it: `inPence`
 * is positive, `outPence` and money set aside are negative.
 */
export const FlowPoint = z.object({
  /** The day the bucket opens — the day itself, or the 1st for a month. */
  at: IsoDate,
  inPence: Pence,
  outPence: Pence,
  savingPence: Pence,
  /**
   * What of this bucket came off a budget, negative as it is stored — the
   * figure that draws the pot down.
   *
   * **Not a slice of `outPence`.** It is booked spending *and* saving in
   * categories with an allowance in force that month, which is how the budgets
   * screen counts what a budget has used: money into a budgeted savings pot is
   * in this and in neither of the two above, and spending nobody budgeted for
   * is in `outPence` and not in this.
   */
  budgetedPence: Pence,
});

/**
 * The period as it happened, in order, rather than as one sum at the end of it.
 *
 * Two figures for a month say what it came to. They cannot say whether the
 * spending was steady, or was one weekend, or has already used three weeks'
 * allowance by the 9th — which is the question somebody looking at a month
 * still in progress is actually asking.
 *
 * `from` and `to` are the axis, not the data: they span the whole period, so a
 * month still running draws its remaining days as space rather than pretending
 * it ended today. `points` stop at the last bucket that could have anything in
 * it. Both ends are null only when there is nothing at all to draw.
 */
export const Flow = z.object({
  /**
   * A day at a time for a period of up to three months; a month at a time
   * beyond that, where a point per day is a thousand points nobody can read.
   */
  granularity: z.enum(["day", "month"]),
  from: IsoDate.nullable(),
  to: IsoDate.nullable(),
  /** Zero-filled: a day with no transactions is a point, not a gap. */
  points: z.array(FlowPoint),
  /**
   * What the household allowed itself across this period: the sum of the
   * monthly allowances in force in each of its months — the pot the
   * `budgetedPence` of each bucket draws down.
   *
   * The same set the budgets screen totals, so for a single month this **is**
   * that screen's `totalAllowancePence` and the pot ends exactly where it says
   * "left".
   *
   * Null when nothing is budgeted, and null for all-time: a pot drawn across
   * every month the ledger has ever held, including the ones before anybody
   * set a budget, would flatter the household rather than inform it.
   */
  budgetPence: Pence.nullable(),
});

export const Dashboard = z.object({
  /** The month, range or all-time span actually resolved, from-and-to inclusive. */
  period: ResolvedPeriod,
  /** Months with any activity, newest first. Drives the month picker. */
  months: z.array(IsoMonth),
  totals: MonthTotals,
  /**
   * The period immediately before this one, for a movement figure. Null when
   * there is none to compare against — all-time has no "before".
   */
  previous: MonthTotals.nullable(),
  running: RunningTotal,
  flow: Flow,
  byCategory: z.array(CategorySlice),
  /**
   * Every account, and what moved through each in the period — **not** only
   * the ones `accountIds` focused the figures on. The card this draws is how
   * someone sees the accounts they are not looking at and switches to one, so
   * narrowing it would take away the way back.
   */
  accounts: z.array(AccountSummary),
  recent: z.array(Transaction),
  uncategorisedCount: z.number().int(),
  unclassified: Unclassified,
  coverage: Coverage,
});

export type DashboardQuery = z.infer<typeof DashboardQuery>;
export type MonthTotals = z.infer<typeof MonthTotals>;
export type RunningTotal = z.infer<typeof RunningTotal>;
export type FlowPoint = z.infer<typeof FlowPoint>;
export type Flow = z.infer<typeof Flow>;
export type CategorySlice = z.infer<typeof CategorySlice>;
export type AccountSummary = z.infer<typeof AccountSummary>;
export type Coverage = z.infer<typeof Coverage>;
export type Unclassified = z.infer<typeof Unclassified>;
export type Dashboard = z.infer<typeof Dashboard>;
